import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useDashboardEvents } from '../contexts/EventContext';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as leadsService from '../services/leadsService';
import * as sharingLinksService from '../services/sharingLinksService';
import * as registrationsService from '../services/registrationsService';

const {
  FiBarChart2,
  FiTrendingUp,
  FiUsers,
  FiCalendar,
  FiTarget,
  FiLink,
  FiMousePointer,
  FiRefreshCw
} = FiIcons;

const LEAD_STATUS_LABELS = {
  new: 'New',
  contacted: 'Contacted',
  '1_1_completed': '1-1 Completed',
  converted: 'Converted'
};

const LEAD_STATUS_COLORS = {
  new: 'bg-blue-500',
  contacted: 'bg-amber-500',
  '1_1_completed': 'bg-purple-500',
  converted: 'bg-emerald-500'
};

const Analytics = () => {
  const { user, role, profile } = useAuth();
  const { events, leads } = useDashboardEvents();
  const [leadSummary, setLeadSummary] = useState(null);
  const [agentAnalytics, setAgentAnalytics] = useState(null);
  const [sharingLinks, setSharingLinks] = useState([]);
  const [linkStats, setLinkStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const userId = user?.id ?? profile?.id;
  const isAdmin = role === 'admin';
  const isAgent = role === 'agent' || isAdmin;

  const userEvents = (events ?? []).filter((e) => isAdmin || (e.agentId === userId));
  const userLeads = (leads ?? []).filter((l) => isAdmin || l.agentId === userId);

  const loadAnalytics = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);

    try {
      const promises = [];

      if (isAgent) {
        promises.push(
          leadsService.getAgentLeadSummary({ role, userId, agentId: isAdmin ? undefined : userId }).then((r) => r.data)
        );
        promises.push(
          sharingLinksService.getAgentAnalytics(userId).then((r) => r.data).catch(() => null)
        );
        promises.push(
          sharingLinksService.listSharingLinks({ role, userId }).then((r) => r.data ?? [])
        );
      } else {
        promises.push(Promise.resolve(null), Promise.resolve(null), Promise.resolve([]));
      }

      const [summary, analytics, links] = await Promise.all(promises);

      setLeadSummary(summary ?? null);
      setAgentAnalytics(analytics ?? null);
      setSharingLinks(Array.isArray(links) ? links : []);

      if (Array.isArray(links) && links.length > 0) {
        const statsMap = {};
        await Promise.all(
          links.slice(0, 20).map(async (link) => {
            const { data } = await sharingLinksService.getShareLinkStats(link.id);
            statsMap[link.id] = data ?? null;
          })
        );
        setLinkStats(statsMap);
      }
    } catch (err) {
      console.error('[Analytics] load error:', err);
      setError(err?.message ?? 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [userId, role, isAdmin, isAgent]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const totalRegistrations = userEvents.reduce((sum, e) => sum + (e.registrations ?? 0), 0);
  const convertedCount = leadSummary?.converted_leads != null ? Number(leadSummary.converted_leads) : userLeads.filter((l) => (l.status || '').toLowerCase() === 'converted').length;
  const totalLeadsForRate = leadSummary?.total_leads != null ? Number(leadSummary.total_leads) : userLeads.length;
  const conversionRate = totalLeadsForRate > 0 ? ((convertedCount / totalLeadsForRate) * 100).toFixed(1) : 0;

  const funnelStatuses = ['new', 'contacted', '1_1_completed', 'converted'];
  const funnelData = funnelStatuses.map((status) => {
    const count = userLeads.filter((l) => (l.status || '').toLowerCase() === status).length;
    const pct = userLeads.length > 0 ? ((count / userLeads.length) * 100).toFixed(0) : 0;
    return { status, label: LEAD_STATUS_LABELS[status] || status, count, percentage: Number(pct) };
  });

  const revenue = leadSummary?.total_revenue != null ? Number(leadSummary.total_revenue) : null;
  const paidLeads = leadSummary?.paid_leads != null ? Number(leadSummary.paid_leads) : null;
  const accountsCreated = leadSummary?.accounts_created != null ? Number(leadSummary.accounts_created) : null;

  const stats = [
    {
      title: 'Total Events',
      value: userEvents.length,
      icon: FiCalendar,
      gradient: 'from-blue-500 to-cyan-500',
      bgLight: 'bg-blue-50'
    },
    {
      title: 'Total Leads',
      value: leadSummary?.total_leads ?? userLeads.length,
      icon: FiUsers,
      gradient: 'from-emerald-500 to-teal-500',
      bgLight: 'bg-emerald-50'
    },
    {
      title: 'Total Registrations',
      value: leadSummary?.total_registrations ?? totalRegistrations,
      icon: FiTarget,
      gradient: 'from-purple-500 to-pink-500',
      bgLight: 'bg-purple-50'
    },
    {
      title: 'Conversion Rate',
      value: `${conversionRate}%`,
      icon: FiTrendingUp,
      gradient: 'from-amber-500 to-orange-500',
      bgLight: 'bg-amber-50'
    },
    ...(revenue != null ? [{
      title: 'Revenue (paid leads)',
      value: `$${revenue.toFixed(2)}`,
      icon: FiTrendingUp,
      gradient: 'from-green-500 to-emerald-500',
      bgLight: 'bg-green-50'
    }] : []),
    ...(paidLeads != null ? [{
      title: 'Paid Leads',
      value: paidLeads,
      icon: FiUsers,
      gradient: 'from-teal-500 to-cyan-500',
      bgLight: 'bg-teal-50'
    }] : []),
    ...(accountsCreated != null ? [{
      title: 'Accounts Created',
      value: accountsCreated,
      icon: FiUsers,
      gradient: 'from-violet-500 to-purple-500',
      bgLight: 'bg-violet-50'
    }] : [])
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
            Analytics
          </h1>
          <p className="text-slate-600 mt-2">Track performance and insights via RPCs and services</p>
        </div>
        <button
          type="button"
          onClick={loadAnalytics}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          <SafeIcon icon={FiRefreshCw} className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-amber-800 text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.08 }}
            className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-600 mb-1">{stat.title}</p>
                <p className="text-3xl font-bold text-slate-900">{stat.value}</p>
              </div>
              <div className={`${stat.bgLight} rounded-xl p-3 text-slate-600`}>
                <SafeIcon icon={stat.icon} className="w-6 h-6" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden"
        >
          <div className="p-6 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-900">Lead conversion funnel</h2>
            <p className="text-sm text-slate-500 mt-1">Counts by status (new → contacted → 1-1 completed → converted)</p>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {funnelData.map(({ status, label, count, percentage }) => (
                <div key={status} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className={`w-3 h-3 rounded-full ${LEAD_STATUS_COLORS[status] ?? 'bg-slate-400'}`} />
                    <span className="text-slate-700 font-medium">{label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-900 font-semibold">{count}</span>
                    <span className="text-slate-500 text-sm">({percentage}%)</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden"
        >
          <div className="p-6 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-900">Event performance</h2>
            <p className="text-sm text-slate-500 mt-1">Registrations per event</p>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {userEvents.slice(0, 8).map((event) => (
                <div key={event.id} className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 truncate">{event.title}</p>
                    <p className="text-sm text-slate-500">{event.date}</p>
                  </div>
                  <div className="text-right ml-4">
                    <p className="font-semibold text-slate-900">{event.registrations ?? 0}</p>
                    <p className="text-xs text-slate-500">registrations</p>
                  </div>
                </div>
              ))}
              {userEvents.length === 0 && (
                <div className="text-center py-8 text-slate-500">No events yet</div>
              )}
            </div>
          </div>
        </motion.div>
      </div>

      {isAgent && (sharingLinks.length > 0 || agentAnalytics) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden"
        >
          <div className="p-6 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-900">Invitation link analytics</h2>
            <p className="text-sm text-slate-500 mt-1">Clicks per link and conversion (click → registration → lead)</p>
          </div>
          <div className="p-6">
            {agentAnalytics && typeof agentAnalytics === 'object' && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                {agentAnalytics.total_clicks != null && (
                  <div className="rounded-xl bg-slate-50 p-4">
                    <p className="text-sm text-slate-600">Total clicks</p>
                    <p className="text-2xl font-bold text-slate-900">{agentAnalytics.total_clicks}</p>
                  </div>
                )}
                {agentAnalytics.total_registrations != null && (
                  <div className="rounded-xl bg-slate-50 p-4">
                    <p className="text-sm text-slate-600">Registrations</p>
                    <p className="text-2xl font-bold text-slate-900">{agentAnalytics.total_registrations}</p>
                  </div>
                )}
                {agentAnalytics.total_conversions != null && (
                  <div className="rounded-xl bg-slate-50 p-4">
                    <p className="text-sm text-slate-600">Conversions</p>
                    <p className="text-2xl font-bold text-slate-900">{agentAnalytics.total_conversions}</p>
                  </div>
                )}
              </div>
            )}
            <div className="space-y-3">
              {sharingLinks.slice(0, 10).map((link) => {
                const stats = linkStats[link.id];
                return (
                  <div key={link.id} className="flex items-center justify-between p-4 rounded-xl border border-slate-200 bg-slate-50/50">
                    <div className="flex items-center gap-3 min-w-0">
                      <SafeIcon icon={FiLink} className="w-5 h-5 text-slate-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="font-mono text-sm text-slate-900 truncate">{link.slug ?? link.id}</p>
                        <p className="text-xs text-slate-500">Event: {link.event_id ?? '—'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 flex-shrink-0">
                      {stats && (
                        <>
                          {stats.click_count != null && (
                            <span className="inline-flex items-center gap-1 text-sm text-slate-600">
                              <SafeIcon icon={FiMousePointer} className="w-4 h-4" />
                              {stats.click_count} clicks
                            </span>
                          )}
                          {stats.registration_count != null && (
                            <span className="text-sm text-slate-600">{stats.registration_count} reg.</span>
                          )}
                        </>
                      )}
                      {!stats && <span className="text-sm text-slate-400">—</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default Analytics;
